import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import invoiceService, { type ConsultationInvoice, type InvoiceItem } from '../../../services/invoiceService';
import { consultationService } from '../../../services/consultationService';
import { patientService } from '../../../services/patientService';
import apiClient from '../../../services/apiClient';
import catalogService from '../../../services/catalogService';
import { ncfTypeService, type NcfType } from '../../../services/ncfService';
import { itbisTypeService, type ItbisType } from '../../../services/itbisTypeService';
import type { Doctor } from '../../../types/api';
import FiscalInvoiceDetails from '../components/FiscalInvoiceDetails';
import NcfTypeSelector from '../components/NcfTypeSelector';
import ConfirmModal from '../../../components/common/ConfirmModal';
import { formatCurrency } from '../../../lib/format';
import { calculateItbis, defaultItbisTypeCode, rateForItbisType } from '../utils/calculateItbis';
import { useClinicRnc } from '../../../hooks/useClinicRnc';
import PermissionGate from '../../../shared/components/PermissionGate';
import type { MedicalConsultation } from '../../../types/api';
import { formatDate } from '../../../lib/format';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Spinner from '../../../components/ui/Spinner';
import { INVOICE_STATUS_LABELS, INVOICE_STATUS_CLASSES } from '../../../domain/statuses';

interface DraftItem {
    description: string;
    quantity: number;
    unit_price: number;
    discount: number;
    itbis_type: string;
    service_id?: number;
    _key: number;
}

interface PatientOption {
    id: number;
    first_name: string;
    last_name: string;
    identification?: string;
    identification_type?: { name?: string };
}

const METHOD_LABELS: Record<string, string> = {
    cash: 'Efectivo', card: 'Tarjeta', insurance: 'Seguro',
    transfer: 'Transferencia', mixed: 'Mixto',
};

let _keyCounter = 0;
const newKey = () => ++_keyCounter;

const InvoicePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const consultationIdParam = searchParams.get('consultation_id');
    const isNew = !id || id === 'new';

    const [invoice, setInvoice] = useState<ConsultationInvoice | null>(null);
    const [consultation, setConsultation] = useState<MedicalConsultation | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Form state (only used when creating)
    const [items, setItems] = useState<DraftItem[]>([
        { description: '', quantity: 1, unit_price: 0, discount: 0, itbis_type: 'gravado_18', _key: newKey() },
    ]);
    const [ncfTypes, setNcfTypes] = useState<NcfType[]>([]);
    const [itbisTypes, setItbisTypes] = useState<ItbisType[]>([]);
    const [ncfTypeId, setNcfTypeId] = useState<number | ''>('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [discount, setDiscount] = useState(0);
    const [insuranceCoverage, setInsuranceCoverage] = useState(0);
    const [notes, setNotes] = useState('');

    const isStandaloneCreate = isNew && !consultationIdParam;
    const [selectedPatient, setSelectedPatient] = useState<PatientOption | null>(null);
    const [patientSearch, setPatientSearch] = useState('');
    const [patientResults, setPatientResults] = useState<PatientOption[]>([]);
    const [selectedDoctorId, setSelectedDoctorId] = useState<number | ''>('');
    const [doctors, setDoctors] = useState<Doctor[]>([]);

    // Service search for items
    const [serviceSearch, setServiceSearch] = useState('');
    const [serviceResults, setServiceResults] = useState<{ id: number; name: string; code: string }[]>([]);
    const [activeItemKey, setActiveItemKey] = useState<number | null>(null);

    // Status update (existing invoice)
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [showRevertConfirm, setShowRevertConfirm] = useState(false);

    const defaultItbis = defaultItbisTypeCode(itbisTypes);
    const subtotal = items.reduce((acc, it) => acc + (it.quantity * it.unit_price) - it.discount, 0);
    const computedTax = items.reduce((acc, it) => {
        const base = (it.quantity * it.unit_price) - it.discount;
        return acc + calculateItbis(rateForItbisType(it.itbis_type, itbisTypes), base);
    }, 0);
    const total = subtotal - discount + computedTax;
    const copay = Math.max(0, total - insuranceCoverage);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (!isNew && id) {
                const inv = await invoiceService.getById(Number(id));
                setInvoice(inv);
                if (inv.consultation_id) {
                    try {
                        const c = await consultationService.getById(inv.consultation_id);
                        setConsultation(c);
                    } catch {
                        // Invoice already carries patient/doctor — do not fail the page on consultation 403
                    }
                }
            } else if (consultationIdParam) {
                const existing = await invoiceService.getByConsultation(Number(consultationIdParam));
                if (existing) {
                    navigate(`/billing/${existing.id}`, { replace: true });
                    return;
                }
                try {
                    const c = await consultationService.getById(Number(consultationIdParam));
                    setConsultation(c);
                    // Pre-fill with consultation fee item
                    setItems([{
                        description: `Consulta médica - ${c.chief_complaint || 'Atención general'}`,
                        quantity: 1,
                        unit_price: 0,
                        discount: 0,
                        itbis_type: 'gravado_18',
                        _key: newKey(),
                    }]);
                } catch (err: unknown) {
                    const status = (err as { response?: { status?: number } })?.response?.status;
                    if (status !== 403) {
                        setError('No se pudo cargar la consulta asociada.');
                    }
                }
            }
        } catch {
            setError('Error al cargar los datos');
        } finally {
            setLoading(false);
        }
    }, [id, isNew, consultationIdParam, navigate]);

    useEffect(() => { loadData(); }, [loadData]);

    useEffect(() => {
        ncfTypeService.getActive().then(setNcfTypes).catch(() => {});
        itbisTypeService.getActive().then(setItbisTypes).catch(() => {});
    }, []);

    useEffect(() => {
        if (!isNew || itbisTypes.length === 0) return;
        const fallback = defaultItbisTypeCode(itbisTypes);
        if (!fallback) return;
        setItems((prev) => prev.map((it) => {
            const valid = itbisTypes.some((t) => t.code === it.itbis_type && t.is_active !== false);
            return valid ? it : { ...it, itbis_type: fallback };
        }));
    }, [itbisTypes, isNew]);

    useEffect(() => {
        if (!isStandaloneCreate) return;
        apiClient.get('/appointments/doctors')
            .then((res: { data?: { data?: Doctor[] } }) => setDoctors(res.data?.data || []))
            .catch(() => { /* reception without appointments still sees the form */ });
    }, [isStandaloneCreate]);

    const searchPatients = useCallback(async (term: string) => {
        if (!term || term.length < 2) {
            setPatientResults([]);
            return;
        }
        try {
            const results = await patientService.search(term);
            setPatientResults((results || []) as PatientOption[]);
        } catch {
            setPatientResults([]);
        }
    }, []);

    useEffect(() => {
        if (!isStandaloneCreate) return;
        if (selectedPatient) return;
        const timer = setTimeout(() => searchPatients(patientSearch), 300);
        return () => clearTimeout(timer);
    }, [isStandaloneCreate, patientSearch, searchPatients, selectedPatient]);

    // Task 5.7: gate the NCF selector behind clinic RNC.
    const { rnc: clinicRnc, loading: clinicRncLoading } = useClinicRnc();
    const showNcfControls = !!clinicRnc && !clinicRncLoading;

    // Auto-detect default NCF type from patient ID type (RNC → B01, else → B02).
    useEffect(() => {
        if (!showNcfControls || ncfTypeId !== '' || ncfTypes.length === 0) return;
        const patient = (consultation?.patient ?? selectedPatient) as { identification_type_id?: number; identification_type?: { name?: string } } | undefined;
        if (!patient) return;
        const idTypeName = patient?.identification_type?.name
            ?? (patient?.identification_type_id === 2 ? 'RNC' : 'Cédula');
        const desiredCode = idTypeName === 'RNC' ? 'B01' : 'B02';
        const match = ncfTypes.find(t => t.code === desiredCode);
        if (match) {
            setNcfTypeId(match.id);
        }
    }, [showNcfControls, ncfTypes, consultation, selectedPatient, ncfTypeId]);

    const searchServices = useCallback(async (term: string) => {
        if (term.length < 2) { setServiceResults([]); return; }
        try {
            const result = await catalogService.getServices({ search: term });
            setServiceResults((result ?? []).map(s => ({ id: s.id, name: s.name, code: s.code ?? '' })));
        } catch { /* ignore */ }
    }, []);

    const updateItem = (key: number, field: keyof Omit<DraftItem, '_key'>, value: string | number) => {
        setItems(prev => prev.map(it => it._key === key ? { ...it, [field]: value } : it));
    };

    const removeItem = (key: number) => {
        setItems(prev => prev.filter(it => it._key !== key));
    };

    const addItem = () => {
        setItems(prev => [...prev, { description: '', quantity: 1, unit_price: 0, discount: 0, itbis_type: defaultItbis || 'gravado_18', _key: newKey() }]);
    };

    const selectService = (itemKey: number, svc: { id: number; name: string; code: string }) => {
        setItems(prev => prev.map(it =>
            it._key === itemKey
                ? { ...it, description: svc.name, service_id: svc.id }
                : it
        ));
        setServiceSearch('');
        setServiceResults([]);
        setActiveItemKey(null);
    };

    const handleSelectPatient = (patient: PatientOption) => {
        setSelectedPatient(patient);
        setPatientSearch(`${patient.first_name} ${patient.last_name}`);
        setPatientResults([]);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const linkedConsultationId = consultationIdParam || consultation?.id;
        if (!linkedConsultationId && (!selectedPatient || !selectedDoctorId)) {
            setError('Debe seleccionar un paciente y un médico');
            return;
        }
        if (items.some(it => !it.description || it.unit_price <= 0)) {
            setError('Todos los ítems deben tener descripción y precio mayor a 0');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const created = await invoiceService.create({
                ...(linkedConsultationId
                    ? { consultation_id: Number(linkedConsultationId) }
                    : { patient_id: selectedPatient!.id, doctor_id: Number(selectedDoctorId) }),
                payment_method: paymentMethod || undefined,
                discount: discount || undefined,
                insurance_coverage: insuranceCoverage || undefined,
                ncf_type_id: ncfTypeId || undefined,
                notes: notes || undefined,
                items: items.map(it => {
                    const base = (it.quantity * it.unit_price) - (it.discount || 0);
                    return {
                        description: it.description,
                        quantity: it.quantity,
                        unit_price: it.unit_price,
                        discount: it.discount || undefined,
                        itbis_type: it.itbis_type || undefined,
                        itbis_amount: calculateItbis(rateForItbisType(it.itbis_type, itbisTypes), base),
                        service_id: it.service_id,
                    };
                }),
            });
            navigate(`/billing/${created.id}`);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Error al crear la factura');
        } finally {
            setSaving(false);
        }
    };

    const updateStatus = async (newStatus: 'draft' | 'paid' | 'cancelled') => {
        if (!invoice) return;
        setUpdatingStatus(true);
        try {
            const updated = await invoiceService.update(invoice.id, { status: newStatus });
            setInvoice(updated);
            setSuccess(`Estado actualizado a: ${INVOICE_STATUS_LABELS[newStatus]}`);
            setTimeout(() => setSuccess(null), 3000);
        } catch {
            setError('Error al actualizar estado');
        } finally {
            setUpdatingStatus(false);
        }
    };

    if (loading) {
        return <Spinner variant="full-page" text="Cargando..." size="lg" />;
    }

    // --- VIEW MODE (existing invoice) ---
    if (!isNew && invoice) {
        return (
            <>
                <div className="page-header">
                    <div className="row">
                        <div className="col-sm-12">
                            <Breadcrumb items={[{ label: 'Facturación', to: '/billing' }, { label: invoice.invoice_number }]} />
                        </div>
                    </div>
                </div>

                {error && <div className="alert alert-danger alert-dismissible"><button className="btn-close" onClick={() => setError(null)}></button>{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}

                <div className="row">
                    <div className="col-md-8">
                        <div className="card">
                            <div className="card-body">
                                {/* Header */}
                                <div className="d-flex justify-content-between align-items-start mb-4">
                                    <div>
                                        <h4 className="card-title mb-1">{invoice.invoice_number}</h4>
                                        <p className="text-muted mb-0">
                                            Emitido: {new Date(invoice.created_at).toLocaleDateString('es-DO')}
                                            {invoice.paid_at && (
                                                <span className="text-success ms-2">
                                                    · Pagado: {formatDate(invoice.paid_at)}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    <span className={`badge bg-${INVOICE_STATUS_CLASSES[invoice.status] || 'secondary'}`} style={{ fontSize: '13px', padding: '6px 14px' }}>
                                        {INVOICE_STATUS_LABELS[invoice.status] || 'Desconocido'}
                                    </span>
                                </div>

                                {/* Patient & Consultation */}
                                <div className="row mb-4">
                                    <div className="col-md-6">
                                        <div className="p-3 bg-light rounded">
                                            <small className="text-uppercase text-muted fw-bold d-block mb-1">Paciente</small>
                                            <strong>
                                                {invoice.patient
                                                    ? `${invoice.patient.first_name} ${invoice.patient.last_name}`
                                                    : '-'}
                                            </strong>
                                            {invoice.patient?.identification && (
                                                <div className="text-muted small">ID: {invoice.patient.identification}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="p-3 bg-light rounded">
                                            <small className="text-uppercase text-muted fw-bold d-block mb-1">Doctor</small>
                                            <strong>
                                                {invoice.doctor
                                                    ? `${invoice.doctor.first_name} ${invoice.doctor.last_name}`
                                                    : '-'}
                                            </strong>
                                            {invoice.doctor?.specialty && (
                                                <div className="text-muted small">{invoice.doctor.specialty.name}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Items Table */}
                                <table className="table table-bordered table-sm">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>#</th>
                                            <th>Descripción</th>
                                            <th className="text-end">Cant.</th>
                                            <th className="text-end">P. Unit.</th>
                                            <th className="text-end">Dto.</th>
                                            <th className="text-end">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(invoice.items || []).map((item: InvoiceItem, i: number) => (
                                            <tr key={item.id}>
                                                <td>{i + 1}</td>
                                                <td>
                                                    <strong>{item.description}</strong>
                                                    {item.service && <div className="text-muted" style={{ fontSize: '11px' }}>Cód: {item.service.code}</div>}
                                                </td>
                                                <td className="text-end">{item.quantity}</td>
                                                <td className="text-end">{formatCurrency(item.unit_price)}</td>
                                                <td className="text-end">{formatCurrency(item.discount) || '-'}</td>
                                                <td className="text-end"><strong>{formatCurrency(item.total)}</strong></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* Totals */}
                                <div className="row justify-content-end">
                                    <div className="col-md-5">
                                        <table className="table table-sm table-borderless mb-0">
                                            <tbody>
                                                <tr>
                                                    <td className="text-muted">Subtotal</td>
                                                    <td className="text-end">{formatCurrency(invoice.subtotal)}</td>
                                                </tr>
                                                {Number(invoice.discount) > 0 && (
                                                    <tr>
                                                        <td className="text-muted">Descuento</td>
                                                        <td className="text-end text-danger">- {formatCurrency(invoice.discount)}</td>
                                                    </tr>
                                                )}
                                                {Number(invoice.tax) > 0 && (
                                                    <tr>
                                                        <td className="text-muted">ITBIS</td>
                                                        <td className="text-end">{formatCurrency(invoice.tax)}</td>
                                                    </tr>
                                                )}
                                                <tr>
                                                    <td className="fw-bold">TOTAL</td>
                                                    <td className="text-end fw-bold">{formatCurrency(invoice.total)}</td>
                                                </tr>
                                                {Number(invoice.insurance_coverage) > 0 && (
                                                    <>
                                                        <tr>
                                                            <td className="text-muted" style={{ paddingLeft: '10px' }}>Cubre Seguro</td>
                                                            <td className="text-end text-info">- {formatCurrency(invoice.insurance_coverage)}</td>
                                                        </tr>
                                                        <tr className="fw-bold">
                                                            <td className="text-info" style={{ paddingLeft: '10px' }}>Copago Paciente</td>
                                                            <td className="text-end text-info">{formatCurrency(invoice.patient_copay)}</td>
                                                        </tr>
                                                    </>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {invoice.payment_method && (
                                    <div className="mt-3 p-2 rounded" style={{ background: '#f0fdfa', border: '1px solid #99f6e4' }}>
                                        <small className="text-muted fw-bold d-block">Forma de Pago</small>
                                        <strong>{METHOD_LABELS[invoice.payment_method] ?? invoice.payment_method}</strong>
                                    </div>
                                )}

                                {invoice.notes && (
                                    <div className="mt-3 p-2 rounded" style={{ background: '#fffbeb', borderLeft: '3px solid #f59e0b' }}>
                                        <small className="fw-bold d-block" style={{ color: '#92400e' }}>Notas</small>
                                        <span>{invoice.notes}</span>
                                    </div>
                                )}

                                {/* Read-only fiscal details for NCF invoices (task 4.7) */}
                                <FiscalInvoiceDetails invoice={invoice} />
                            </div>
                        </div>
                    </div>

                    {/* Sidebar actions */}
                    <div className="col-md-4">
                        <div className="card">
                            <div className="card-body">
                                <h6 className="card-title">Acciones</h6>
                                <div className="d-grid gap-2">
                                    <button
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => invoiceService.downloadPdf(invoice.id, invoice.invoice_number)}
                                    >
                                        <i className="fa fa-download me-1"></i> Descargar PDF
                                    </button>
                                    {invoice.status === 'draft' && (
                                        <>
                                            <button
                                                className="btn btn-success btn-sm"
                                                onClick={() => updateStatus('paid')}
                                                disabled={updatingStatus}
                                            >
                                                <i className="fa fa-check me-1"></i> Marcar como Pagado
                                            </button>
                                            <PermissionGate module="billing" action="anular">
<button
                                                className="btn btn-danger btn-sm"
                                                onClick={() => setShowCancelConfirm(true)}
                                                disabled={updatingStatus}
                                            >
                                                <i className="fa fa-ban me-1"></i> Anular
                                            </button>
                                            </PermissionGate>
                                        </>
                                    )}
                                    {invoice.status === 'paid' && (
<button
                                        className="btn btn-warning btn-sm"
                                        onClick={() => setShowRevertConfirm(true)}
                                        disabled={updatingStatus}
                                    >
                                        <i className="fa fa-undo me-1"></i> Revertir a Borrador
                                    </button>
                                    )}
                                    {invoice.consultation_id && (
                                        <Link
                                            to={`/consultations/${invoice.consultation_id}`}
                                            className="btn btn-outline-secondary btn-sm"
                                        >
                                            <i className="fa fa-stethoscope me-1"></i> Ver Consulta
                                        </Link>
                                    )}
                                    <Link to="/billing" className="btn btn-outline-secondary btn-sm">
                                        <i className="fa fa-arrow-left me-1"></i> Volver a Facturación
                                    </Link>
                                </div>

                                {typeof invoice.issued_by === 'object' && invoice.issued_by?.name && (
                                    <div className="mt-3 text-muted small">
                                        <i className="fa fa-user me-1"></i>
                                        Emitido por: <strong>{invoice.issued_by.name}</strong>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <ConfirmModal
                    show={showCancelConfirm}
                    title="Anular factura"
                    body="¿Está seguro que desea anular esta factura? Esta acción no se puede deshacer."
                    confirmLabel="Anular"
                    cancelLabel="Cancelar"
                    confirmVariant="danger"
                    loading={updatingStatus}
                    onConfirm={() => {
                        setShowCancelConfirm(false);
                        void updateStatus('cancelled');
                    }}
                    onCancel={() => setShowCancelConfirm(false)}
                />

                <ConfirmModal
                    show={showRevertConfirm}
                    title="Revertir a borrador"
                    body="¿Está seguro que desea regresar esta factura a estado borrador?"
                    confirmLabel="Revertir"
                    cancelLabel="Cancelar"
                    confirmVariant="warning"
                    loading={updatingStatus}
                    onConfirm={() => {
                        setShowRevertConfirm(false);
                        void updateStatus('draft');
                    }}
                    onCancel={() => setShowRevertConfirm(false)}
                />
            </>
        );
    }

    // --- CREATE MODE ---
    const consultationForDisplay = consultation;

    return (
        <>
            <div className="page-header">
                <div className="row">
                    <div className="col-sm-12">
                        <Breadcrumb items={[{ label: 'Facturación', to: '/billing' }, { label: 'Nueva Factura' }]} />
                    </div>
                </div>
            </div>

            {error && <div className="alert alert-danger alert-dismissible"><button className="btn-close" onClick={() => setError(null)}></button>{error}</div>}

            <form onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-md-8">
                        <div className="card">
                            <div className="card-body">
                                <h4 className="card-title mb-3">Nueva Factura</h4>

                                {consultationForDisplay && (
                                    <div className="alert alert-info mb-3">
                                        <strong>Consulta:</strong> {formatDate(consultationForDisplay.consultation_date)} — {consultationForDisplay.chief_complaint}
                                        {consultationForDisplay.patient && (
                                            <span> | <strong>Paciente:</strong> {(consultationForDisplay.patient as any)?.first_name} {(consultationForDisplay.patient as any)?.last_name}</span>
                                        )}
                                    </div>
                                )}

                                {isStandaloneCreate && (
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label form-label-sm" htmlFor="invoice-patient-search">Paciente *</label>
                                            <div className="position-relative">
                                                <input
                                                    id="invoice-patient-search"
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Buscar paciente por nombre o cédula..."
                                                    value={patientSearch}
                                                    onChange={e => {
                                                        setPatientSearch(e.target.value);
                                                        if (selectedPatient) setSelectedPatient(null);
                                                    }}
                                                />
                                                {patientResults.length > 0 && !selectedPatient && (
                                                    <ul className="list-group mt-1 position-absolute w-100 shadow-sm" style={{ zIndex: 20, maxHeight: 200, overflow: 'auto' }}>
                                                        {patientResults.map(p => (
                                                            <li
                                                                key={p.id}
                                                                className="list-group-item list-group-item-action"
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => handleSelectPatient(p)}
                                                            >
                                                                {p.first_name} {p.last_name}
                                                                {p.identification ? ` - ${p.identification}` : ''}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                            {selectedPatient && (
                                                <small className="text-muted">
                                                    Seleccionado: {selectedPatient.first_name} {selectedPatient.last_name}
                                                </small>
                                            )}
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label form-label-sm" htmlFor="invoice-doctor">Médico solicitante *</label>
                                            <select
                                                id="invoice-doctor"
                                                className="form-select form-select-sm"
                                                value={selectedDoctorId}
                                                onChange={e => setSelectedDoctorId(e.target.value ? Number(e.target.value) : '')}
                                            >
                                                <option value="">Seleccionar médico...</option>
                                                {doctors.map(d => (
                                                    <option key={d.id} value={d.id}>
                                                        Dr. {d.first_name} {d.last_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* Items */}
                                <h6 className="mb-2">Ítems / Servicios</h6>
                                {items.map((item, idx) => (
                                    <div key={item._key} className="border rounded p-3 mb-2 position-relative">
                                        <div className="row g-2">
                                            <div className="col-md-5">
                                                <label className="form-label form-label-sm mb-1">Descripción *</label>
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={item.description}
                                                        onChange={e => {
                                                            updateItem(item._key, 'description', e.target.value);
                                                            setActiveItemKey(item._key);
                                                            searchServices(e.target.value);
                                                            setServiceSearch(e.target.value);
                                                        }}
                                                        placeholder="Descripción del servicio"
                                                        required
                                                    />
                                                    {activeItemKey === item._key && serviceResults.length > 0 && (
                                                        <div className="position-absolute bg-white border rounded shadow-sm" style={{ top: '100%', left: 0, right: 0, zIndex: 100, maxHeight: '180px', overflowY: 'auto' }}>
                                                            {serviceResults.map(s => (
                                                                <div
                                                                    key={s.id}
                                                                    className="px-3 py-2 cursor-pointer"
                                                                    style={{ cursor: 'pointer', fontSize: '13px' }}
                                                                    onMouseDown={() => selectService(item._key, s)}
                                                                >
                                                                    <strong>{s.name}</strong> <span className="text-muted">({s.code})</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label form-label-sm mb-1">Cantidad</label>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    min="1"
                                                    value={item.quantity}
                                                    onChange={e => updateItem(item._key, 'quantity', Number(e.target.value))}
                                                />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label form-label-sm mb-1" htmlFor={`item-price-${item._key}`}>Precio Unit.</label>
                                                <input
                                                    id={`item-price-${item._key}`}
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    min="0"
                                                    step="0.01"
                                                    value={item.unit_price}
                                                    onChange={e => updateItem(item._key, 'unit_price', Number(e.target.value))}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label form-label-sm mb-1">Descuento</label>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    min="0"
                                                    step="0.01"
                                                    value={item.discount}
                                                    onChange={e => updateItem(item._key, 'discount', Number(e.target.value))}
                                                />
                                             </div>
                                             <div className="col-md-2">
                                                 <label className="form-label form-label-sm mb-1" htmlFor={`item-itbis-${item._key}`}>ITBIS</label>
                                                 <select
                                                     id={`item-itbis-${item._key}`}
                                                     className="form-select form-select-sm"
                                                     value={item.itbis_type}
                                                     onChange={e => updateItem(item._key, 'itbis_type', e.target.value)}>
                                                     {itbisTypes.map((t) => (
                                                         <option key={t.code} value={t.code}>
                                                             {t.name} ({(Number(t.rate) * 100).toFixed(0)}%)
                                                         </option>
                                                     ))}
                                                 </select>
                                             </div>
                                             <div className="col-md-1 d-flex align-items-end">
                                                {items.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => removeItem(item._key)}
                                                    >
                                                        <i className="fa fa-times"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-end mt-1">
                                            <small className="text-muted">
                                                Subtotal: <strong>
                                                   {formatCurrency(((item.quantity * item.unit_price) - item.discount))}
                                                </strong>
                                            </small>
                                        </div>
                                    </div>
                                ))}

                                <button type="button" className="btn btn-outline-primary btn-sm mb-3" onClick={addItem}>
                                    <i className="fa fa-plus me-1"></i> Agregar ítem
                                </button>

                                {/* Notes */}
                                <div className="mb-3">
                                    <label className="form-label form-label-sm">Notas</label>
                                    <textarea
                                        className="form-control form-control-sm"
                                        rows={2}
                                        value={notes}
                                        onChange={e => setNotes(e.target.value)}
                                        placeholder="Notas adicionales..."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Summary Sidebar */}
                    <div className="col-md-4">
                        <div className="card">
                            <div className="card-body">
                                <h6 className="card-title">Resumen</h6>

                                <NcfTypeSelector
                                    ncfTypes={ncfTypes}
                                    value={ncfTypeId}
                                    onChange={setNcfTypeId}
                                    hasRnc={showNcfControls}
                                />

                                <div className="mb-3">
                                    <label className="form-label form-label-sm">Forma de Pago</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={paymentMethod}
                                        onChange={e => setPaymentMethod(e.target.value)}
                                    >
                                        <option value="">Seleccionar...</option>
                                        <option value="cash">Efectivo</option>
                                        <option value="card">Tarjeta</option>
                                        <option value="insurance">Seguro</option>
                                        <option value="transfer">Transferencia</option>
                                        <option value="mixed">Mixto</option>
                                    </select>
                                </div>

                                <div className="mb-2">
                                    <label className="form-label form-label-sm">Descuento General</label>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        min="0"
                                        step="0.01"
                                        value={discount}
                                        onChange={e => setDiscount(Number(e.target.value))}
                                    />
                                </div>

                                <div className="mb-2">
                                    <label className="form-label form-label-sm">ITBIS (auto-calculado)</label>
                                    <input type="number" className="form-control form-control-sm" disabled
                                        value={computedTax} />
                                    <small className="form-text text-muted">Suma automática del ITBIS por ítem</small>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label form-label-sm">Cobertura del Seguro</label>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        min="0"
                                        step="0.01"
                                        value={insuranceCoverage}
                                        onChange={e => setInsuranceCoverage(Number(e.target.value))}
                                    />
                                </div>

                                <hr />

                                <table className="table table-sm table-borderless mb-3">
                                    <tbody>
                                        <tr>
                                            <td className="text-muted">Subtotal</td>
                                            <td className="text-end">{formatCurrency(subtotal)}</td>
                                        </tr>
                                        {discount > 0 && (
                                            <tr>
                                                <td className="text-muted">Descuento</td>
                                                <td className="text-end text-danger">- {formatCurrency(discount)}</td>
                                            </tr>
                                        )}
                                        {computedTax > 0 && (
                                            <tr>
                                                <td className="text-muted">ITBIS</td>
                                                <td className="text-end">{formatCurrency(computedTax)}</td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td className="fw-bold">TOTAL</td>
                                            <td className="text-end fw-bold">{formatCurrency(total)}</td>
                                        </tr>
                                        {insuranceCoverage > 0 && (
                                            <>
                                                <tr>
                                                    <td className="text-muted small">Cubre Seguro</td>
                                                    <td className="text-end text-info small">- {formatCurrency(insuranceCoverage)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="text-info fw-bold small">Copago</td>
                                                    <td className="text-end text-info fw-bold small">{formatCurrency(copay)}</td>
                                                </tr>
                                            </>
                                        )}
                                    </tbody>
                                </table>

                                <div className="d-grid gap-2">
                                    <PermissionGate module="billing" action="create">
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={saving || items.length === 0}
                                        >
                                            {saving
                                                ? <Spinner size="sm" text="Guardando..." />
                                                : <><i className="fa fa-save me-1"></i> Crear Factura</>}
                                        </button>
                                    </PermissionGate>
                                    <Link to="/billing" className="btn btn-outline-secondary">
                                        Cancelar
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </>
    );
};

export default InvoicePage;
